import { useTranslations } from '@vocab/react';
import loadable from 'sku/@loadable/component';

import { SITE } from 'src/app/shared/constants/configObjectProperties';
import { IntercomConfiguration } from 'src/app/shared/intercom';
import { getConfig } from 'src/app/utils/config';
import { isProd } from 'src/app/utils/environment';
import { PageViewTracker } from 'src/app/v1/dataAnalytics/PageViewTracker';
import { withAppEnhancers } from 'src/app/v1/views/enhancers/withAppEnhancers';
import { useCopyJob } from 'src/app/v1/views/hooks/useCopyJob';
import { useCreateJob } from 'src/app/v1/views/hooks/useCreateJob';
import { useEditDraft } from 'src/app/v1/views/hooks/useEditDraft';
import { useJobId } from 'src/app/v1/views/hooks/useJobId';
import { useLastValidRoute } from 'src/app/v1/views/hooks/useLastValidRoute';
import { useNavigationBlockerCreateFlow } from 'src/app/v1/views/hooks/useNavigationBlockerCreateFlow';
import { usePageId } from 'src/app/v1/views/hooks/usePageId';
import { useRefreshAdProductSet } from 'src/app/v1/views/hooks/useRefreshProductSetForAdPosting';
import { useSelectedProduct } from 'src/app/v1/views/hooks/useSelectedProduct';
import { useSelectedRRQuestions } from 'src/app/v1/views/hooks/useSelectedRRQuestions';
import { useShoppingCartForAdPosting } from 'src/app/v1/views/hooks/useShoppingCartForAdPosting';
import { useTealiumCreateFlowType } from 'src/app/v1/views/hooks/useTealiumCreateFlowType';
import { useUpdateDraftIdParam } from 'src/app/v1/views/hooks/useUpdateDraftIdParam';
import { SeoTitle } from 'src/app/v1/views/shared/SeoTitle';
import { useHirerAccountAndUserDataQuery } from 'src/graphql/generated';
import type { ContainerProps } from 'src/types/app';

import { useBackfillCart } from '../../hooks/useBackfillCart';
import translations from '../.vocab';

const TrackingDrawer = loadable(
  () =>
    import('src/app/shared/dataAnalytics/components/TrackingDrawer').then(
      (module) => ({
        default: module.TrackingDrawer,
      }),
    ),
  { ssr: false },
);

const CreateFlowContainer = ({ children }: ContainerProps): JSX.Element => {
  useJobId();
  useRefreshAdProductSet();
  useCreateJob();
  useSelectedRRQuestions();
  useSelectedProduct();
  useUpdateDraftIdParam();
  useNavigationBlockerCreateFlow();
  useShoppingCartForAdPosting();
  useBackfillCart();

  return <>{children}</>;
};

export const CreateFlowWithInitialData = (props: ContainerProps) => {
  const { t } = useTranslations(translations);

  usePageId();
  const { loading: isEditDraftLoading } = useEditDraft();
  const { loading: isCopyJobLoading } = useCopyJob();
  const { loading: isLastValidRouteLoading } = useLastValidRoute();
  // Prefetching the data so pay and post v2 can load instantly
  useHirerAccountAndUserDataQuery({
    variables: { advertiserNation: 'deprecated' },
    fetchPolicy: 'cache-first',
  });

  useTealiumCreateFlowType();
  return (
    <>
      <SeoTitle title={t('create-container-seo-title')} />
      <PageViewTracker />
      <IntercomConfiguration site={getConfig(SITE)} />
      {isEditDraftLoading ||
      isCopyJobLoading ||
      isLastValidRouteLoading ? null : (
        <CreateFlowContainer {...props} />
      )}
      {!isProd() && <TrackingDrawer />}
    </>
  );
};

export default withAppEnhancers(CreateFlowWithInitialData);
